// Variant landing-page support.
//
// startviralgpt.com is a separate landing-page domain used for A/B testing.
// It serves the SAME build as viralgpt.com, but at "/" it renders a cloned
// landing page (HomeVariant). Every CTA on that page routes back to the real
// site (viralgpt.com) so the actual signup/checkout funnel always happens
// there — only the landing page lives on the variant domain.

import { CLICK_ID_PARAMS, VARIANT_ENTRY_PARAM, getFunnel } from "./tracking";

export const MAIN_SITE = "https://viralgpt.com";
// Where to send a variant-funnel visitor when they click the logo on the main
// domain — back to the page they entered from, not viralgpt.com's home.
export const VARIANT_SITE = "https://startviralgpt.com";

// Hosts that should render the variant landing page.
export const VARIANT_HOSTS = ["startviralgpt.com", "www.startviralgpt.com"];

// ?variant=1 renders the startviralgpt.com page on any host so it can be
// previewed locally. Real traffic is unaffected — production URLs never carry it.
const variantPreview = new URLSearchParams(window.location.search).get("variant") === "1";

export const isVariantHost =
  VARIANT_HOSTS.includes(window.location.hostname) || variantPreview;

const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((c) => c.startsWith(name + "="))
    ?.split("=")[1] || "";

// Build a CTA URL that points at the main site (viralgpt.com) and forwards the
// current query string for attribution.
//
// Why forward params: the affiliate cookie (?r=) and ad-click ids (ttclid,
// fbclid) are written with domain=.viralgpt.com (see LanguageContext.js /
// Home.js). The browser rejects those cookies on startviralgpt.com, so the
// only way to carry attribution across the domain hop is on the link itself.
// When the visitor lands on viralgpt.com the existing cookie-writing logic
// runs there with the forwarded params intact.
//
// The link's own params (e.g. /register?purchase=basic) take precedence over
// forwarded ones on key collisions.
export const mainUrl = (path = "/") => {
  const incoming = new URLSearchParams(window.location.search);
  const [base, ownQuery = ""] = path.split("?");
  const merged = new URLSearchParams(ownQuery);
  incoming.forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  // Fall back to the first-party affiliate cookie so attribution survives even
  // if ?r= has dropped from the URL while browsing the landing domain. The
  // value is written host-only on startviralgpt.com by persistAffiliate().
  if (!merged.has("r")) {
    const aff = getCookie("affiliate");
    if (aff) merged.set("r", aff);
  }
  // Same fallback for the click ids so TikTok/Meta attribution survives to the
  // main site even if the param dropped while browsing the landing domain.
  CLICK_ID_PARAMS.forEach((name) => {
    if (!merged.has(name)) {
      const v = getCookie(name);
      if (v) merged.set(name, v);
    }
  });
  // Marks the hop as coming from the variant funnel. sessionStorage is
  // per-origin, so the funnel tag set on startviralgpt.com can't be read on
  // viralgpt.com — the marker has to travel on the URL.
  if (isVariantHost && !merged.has(VARIANT_ENTRY_PARAM)) {
    merged.set(VARIANT_ENTRY_PARAM, "1");
  }
  // Carry the funnel tag across the domain hop for the same reason —
  // detectFunnel() honors this param.
  if (!merged.has("funnel")) {
    const f = getFunnel();
    if (f && f.startsWith("50off")) merged.set("funnel", f);
  }
  const qs = merged.toString();
  return `${MAIN_SITE}${base}${qs ? `?${qs}` : ""}`;
};
